import React, { useState, useEffect, useMemo } from "react";
import {
  FiEye, FiSearch, FiUser, FiChevronsUp, FiChevronsDown,
  FiChevronLeft, FiChevronRight, FiFileText, FiEdit,
  FiTrash2, FiPrinter,
} from "react-icons/fi";

import Topbar from "../../component/Topbar";
import Sidebar from "../../component/Sidebar";
import AddCreditNote from "./AddCreditNote";
import { baseurl } from "../../baseurl";
import axios from "axios";
import Swal from "sweetalert2";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

/* ── vars ── */
:root {
  --cn-teal:       #0a5556;
  --cn-teal-mid:   #0c7172;
  --cn-teal-lite:  #14b8a6;
  --cn-teal-faint: rgba(10,85,86,0.06);
  --cn-teal-glow:  rgba(10,85,86,0.13);
  --cn-gold:       #FFD166;
  --cn-gold-dim:   rgba(255,209,102,0.18);
  --cn-bg:         #eef6f6;
  --cn-white:      #ffffff;
  --cn-border:     rgba(10,85,86,0.10);
  --cn-text:       #0f2a2a;
  --cn-muted:      #5a8080;
  --cn-danger:     #ef4444;
  --cn-success:    #10b981;
  --cn-warning:    #f59e0b;
  --cn-info:       #3b82f6;
  --cn-radius:     16px;
}

@keyframes cn__fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes cn__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes cn__pulse   { 0%,100%{opacity:1} 50%{opacity:0.45} }
@keyframes cn__spin    { to{transform:rotate(360deg)} }

/* ── page layout ── */
.cn__wrap {
  font-family: 'DM Sans', sans-serif;
  padding: 22px 22px 40px;
  min-height: 100vh;
  background: var(--cn-bg);
  animation: cn__fadeUp 0.4s ease both;
}

/* ── page header ── */
.cn__hd {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 24px; flex-wrap: wrap;
}
.cn__hd-left { display: flex; align-items: center; gap: 14px; }
.cn__hd-icon {
  width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--cn-teal), var(--cn-teal-lite));
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: #fff;
  box-shadow: 0 6px 18px rgba(10,85,86,0.26);
}
.cn__hd-title {
  font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 800;
  color: var(--cn-text); letter-spacing: -0.03em; line-height: 1.1; margin: 0 0 3px;
}
.cn__hd-sub { font-size: 12.5px; color: var(--cn-muted); margin: 0; }

/* ── stat chips ── */
.cn__stats {
  display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap;
  animation: cn__fadeUp 0.45s 0.05s ease both;
}
.cn__stat {
  display: flex; align-items: center; gap: 10px;
  background: var(--cn-white); border: 1px solid var(--cn-border);
  border-radius: 13px; padding: 11px 16px;
  box-shadow: 0 2px 10px rgba(10,85,86,0.05);
  flex: 1; min-width: 140px;
}
.cn__stat-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.cn__stat-label { font-size: 11.5px; color: var(--cn-muted); font-weight: 500; }
.cn__stat-val   { font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; color: var(--cn-text); }

/* ── main card ── */
.cn__card {
  background: var(--cn-white);
  border-radius: 20px;
  border: 1px solid var(--cn-border);
  box-shadow: 0 2px 20px rgba(10,85,86,0.07), 0 8px 40px rgba(0,0,0,0.04);
  overflow: hidden;
  animation: cn__fadeUp 0.5s 0.08s ease both;
  position: relative;
}
.cn__card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--cn-teal) 0%, var(--cn-teal-lite) 55%, var(--cn-gold) 100%);
}

/* ── toolbar ── */
.cn__toolbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px; padding: 18px 22px; flex-wrap: wrap;
  border-bottom: 1px solid var(--cn-border);
}
.cn__toolbar-left  { display: flex; align-items: center; gap: 10px; }
.cn__toolbar-right { display: flex; align-items: center; gap: 10px; }

.cn__show-lbl { font-size: 12.5px; color: var(--cn-muted); font-weight: 500; white-space: nowrap; }
.cn__select {
  padding: 7px 11px; font-size: 13px; font-family: 'DM Sans', sans-serif;
  color: var(--cn-text); background: var(--cn-bg);
  border: 1.5px solid rgba(10,85,86,0.15); border-radius: 9px;
  outline: none; cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.cn__select:focus { border-color: var(--cn-teal); box-shadow: 0 0 0 3px rgba(10,85,86,0.09); }

.cn__search {
  display: flex; align-items: center; gap: 8px;
  background: var(--cn-bg); border: 1.5px solid rgba(10,85,86,0.15);
  border-radius: 10px; padding: 7px 13px;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.cn__search:focus-within { border-color: var(--cn-teal); box-shadow: 0 0 0 3px rgba(10,85,86,0.09); background: #fff; }
.cn__search-icon { color: var(--cn-muted); font-size: 14px; flex-shrink: 0; }
.cn__search-input {
  border: none; outline: none; background: transparent;
  font-size: 13px; font-family: 'DM Sans', sans-serif; color: var(--cn-text);
  width: 200px;
}
.cn__search-input::placeholder { color: #9ababa; }

/* ── table ── */
.cn__table-wrap { overflow-x: auto; }
.cn__table {
  width: 100%; border-collapse: collapse;
}
.cn__table thead th {
  font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.09em; text-transform: uppercase; color: var(--cn-teal);
  background: rgba(10,85,86,0.04); padding: 12px 18px;
  white-space: nowrap; border-bottom: 1px solid var(--cn-border);
  user-select: none;
}
.cn__table thead th.sortable { cursor: pointer; }
.cn__table thead th.sortable:hover { color: var(--cn-teal-lite); }

.cn__sort-icon { display: inline-flex; vertical-align: middle; margin-left: 4px; opacity: 0.35; font-size: 13px; }
.cn__sort-icon--active { opacity: 1; color: var(--cn-teal-lite); }

.cn__table tbody tr {
  border-bottom: 1px solid rgba(10,85,86,0.06);
  transition: background 0.15s;
}
.cn__table tbody tr:last-child { border-bottom: none; }
.cn__table tbody tr:hover { background: rgba(10,85,86,0.025); }
.cn__table tbody td { padding: 13px 18px; font-size: 13.5px; color: var(--cn-text); vertical-align: middle; }

/* cell helpers */
.cn__note-id {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 700;
  background: rgba(10,85,86,0.07); color: var(--cn-teal);
  border: 1px solid rgba(10,85,86,0.14); border-radius: 7px;
  padding: 3px 10px; white-space: nowrap;
}
.cn__customer {
  display: flex; align-items: center; gap: 8px;
}
.cn__cust-avatar {
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--cn-teal), var(--cn-teal-lite));
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #fff;
  font-family: 'Sora', sans-serif;
}
.cn__cust-name  { font-size: 13px; font-weight: 600; color: var(--cn-text); }
.cn__inv-chip {
  display: inline-flex; align-items: center;
  font-size: 11.5px; color: var(--cn-muted);
  background: rgba(10,85,86,0.05); border: 1px solid rgba(10,85,86,0.11);
  border-radius: 6px; padding: 2px 8px;
}
.cn__date { font-size: 12.5px; color: var(--cn-muted); }
.cn__amount {
  font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 800;
  color: var(--cn-danger);
}

/* status badge */
.cn__badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 700; font-family: 'Sora', sans-serif;
  border-radius: 50px; padding: 3px 11px; white-space: nowrap;
}
.cn__badge-dot { width: 6px; height: 6px; border-radius: 50%; }
.cn__badge--applied  { background: rgba(16,185,129,0.10); color: #059669; border: 1px solid rgba(16,185,129,0.22); }
.cn__badge--applied .cn__badge-dot  { background: #059669; }
.cn__badge--cancelled{ background: rgba(239,68,68,0.08);  color: #dc2626; border: 1px solid rgba(239,68,68,0.20); }
.cn__badge--cancelled .cn__badge-dot{ background: #dc2626; }
.cn__badge--pending  { background: rgba(59,130,246,0.09); color: #2563eb; border: 1px solid rgba(59,130,246,0.20); }
.cn__badge--pending .cn__badge-dot  { background: #2563eb; }

/* actions */
.cn__actions { display: flex; align-items: center; gap: 4px; justify-content: center; }
.cn__action-btn {
  width: 30px; height: 30px; border-radius: 8px; border: none;
  background: transparent; display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: var(--cn-muted); cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.cn__action-btn:hover          { background: rgba(10,85,86,0.08); color: var(--cn-teal); }
.cn__action-btn--danger:hover  { background: rgba(239,68,68,0.09); color: var(--cn-danger); }

/* empty state */
.cn__empty {
  padding: 60px 20px; text-align: center;
}
.cn__empty-icon {
  width: 60px; height: 60px; border-radius: 18px; margin: 0 auto 16px;
  background: rgba(10,85,86,0.07);
  display: flex; align-items: center; justify-content: center;
  font-size: 26px;
}
.cn__empty-title { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: var(--cn-text); margin-bottom: 6px; }
.cn__empty-sub   { font-size: 13px; color: var(--cn-muted); }

/* loading shimmer */
.cn__shimmer-row td { padding: 14px 18px; }
.cn__shimmer-cell {
  height: 14px; border-radius: 6px;
  background: linear-gradient(90deg, #e8f4f4 0%, #d0eded 50%, #e8f4f4 100%);
  background-size: 600px 100%;
  animation: cn__shimmer 1.4s linear infinite;
}

/* ── footer ── */
.cn__footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 22px; border-top: 1px solid var(--cn-border);
  flex-wrap: wrap; gap: 10px;
}
.cn__footer-info { font-size: 12.5px; color: var(--cn-muted); }
.cn__footer-info strong { color: var(--cn-text); }

/* ── pagination ── */
.cn__pagination { display: flex; align-items: center; gap: 4px; }
.cn__page-btn {
  width: 32px; height: 32px; border-radius: 9px; border: 1.5px solid var(--cn-border);
  background: var(--cn-white); font-size: 12.5px; font-weight: 600;
  color: var(--cn-muted); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.cn__page-btn:hover:not(:disabled) { border-color: var(--cn-teal); color: var(--cn-teal); background: rgba(10,85,86,0.05); }
.cn__page-btn--active { background: var(--cn-teal) !important; border-color: var(--cn-teal) !important; color: #fff !important; font-family: 'Sora', sans-serif; }
.cn__page-btn:disabled { opacity: 0.38; cursor: not-allowed; }
.cn__page-btn--nav { width: auto; padding: 0 10px; gap: 4px; font-size: 12px; }
`;

/* ─── Helpers ─── */
const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 });

const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

function StatusBadge({ status }) {
  const map = {
    Applied:   "cn__badge--applied",
    Cancelled: "cn__badge--cancelled",
  };
  const cls = map[status] || "cn__badge--pending";
  return (
    <span className={`cn__badge ${cls}`}>
      <span className="cn__badge-dot" />
      {status || "Pending"}
    </span>
  );
}

function CustomPagination({ totalItems, itemsPerPage, currentPage, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems <= itemsPerPage) return null;
  return (
    <div className="cn__pagination">
      <button className="cn__page-btn cn__page-btn--nav" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        <FiChevronLeft /> Prev
      </button>
      {Array.from({ length: totalPages }).map((_, i) => (
        <button key={i} className={`cn__page-btn ${currentPage === i + 1 ? "cn__page-btn--active" : ""}`} onClick={() => onPageChange(i + 1)}>
          {i + 1}
        </button>
      ))}
      <button className="cn__page-btn cn__page-btn--nav" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        Next <FiChevronRight />
      </button>
    </div>
  );
}

/* ─── Main Component ─── */
function CreditNote() {
  const token = localStorage.getItem("auth_token");

  const [creditNotes, setCreditNotes]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState("");
  const [sortBy, setSortBy]             = useState({ key: "credit_date", direction: "desc" });
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editData, setEditData]         = useState(null);

  useEffect(() => { fetchCreditNotes(); }, []);

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getAllCreditNotesByBusiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreditNotes(res.data.data || []);
    } catch (err) {
      console.error("Failed to load credit notes", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, invoice_id) => {
    const confirm = await Swal.fire({
      title: "Delete Credit Note?",
      text: "Credited amount will be added back to the invoice total.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    });
    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteCreditNote`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { id, invoice_id },
        });
        Swal.fire("Deleted!", "Credit note removed.", "success");
        fetchCreditNotes();
      } catch (err) {
        Swal.fire("Error!", "Failed to delete.", "error");
      }
    }
  };

  const processedCreditNotes = useMemo(() => {
    let list = [...creditNotes];
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      list = list.filter(i =>
        i.credit_note_number?.toLowerCase().includes(s) ||
        i.customer_name?.toLowerCase().includes(s) ||
        i.invoice_number?.toLowerCase().includes(s)
      );
    }
    list.sort((a, b) => {
      let aVal = a[sortBy.key];
      let bVal = b[sortBy.key];
      if (sortBy.key === "credit_amount") return sortBy.direction === "asc" ? aVal - bVal : bVal - aVal;
      if (sortBy.key === "credit_date")   return sortBy.direction === "asc" ? new Date(aVal) - new Date(bVal) : new Date(bVal) - new Date(aVal);
      aVal = String(aVal || "").toLowerCase();
      bVal = String(bVal || "").toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [creditNotes, searchTerm, sortBy]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated  = processedCreditNotes.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortBy((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };

  const SortIcon = ({ columnKey }) => {
    const isActive = sortBy.key === columnKey;
    const cls = `cn__sort-icon${isActive ? " cn__sort-icon--active" : ""}`;
    return isActive && sortBy.direction === "asc"
      ? <FiChevronsUp className={cls} />
      : <FiChevronsDown className={cls} />;
  };

  /* stat counts */
  const totalAmt    = creditNotes.reduce((s, n) => s + Number(n.credit_amount || 0), 0);
  const appliedCnt  = creditNotes.filter((n) => n.status === "active").length;
  const pendingCnt  = creditNotes.filter((n) => n.status !== "Applied" && n.status !== "Cancelled").length;

  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="cn__wrap">

          {/* ── Page Header ── */}
          <div className="cn__hd">
            <div className="cn__hd-left">
              <div className="cn__hd-icon"><FiFileText /></div>
              <div>
                <div className="cn__hd-title">Credit Notes</div>
                <div className="cn__hd-sub">Manage issued and applied credit notes</div>
              </div>
            </div>
            <AddCreditNote onSuccess={fetchCreditNotes} editData={editData} clearEdit={() => setEditData(null)} />
          </div>

          {/* ── Stats ── */}
          <div className="cn__stats">
            <div className="cn__stat">
              <div className="cn__stat-dot" style={{ background: "#0a5556" }} />
              <div>
                <div className="cn__stat-label">Total Notes</div>
                <div className="cn__stat-val">{creditNotes.length}</div>
              </div>
            </div>
            <div className="cn__stat">
              <div className="cn__stat-dot" style={{ background: "#10b981" }} />
              <div>
                <div className="cn__stat-label">Active</div>
                <div className="cn__stat-val">{appliedCnt}</div>
              </div>
            </div>
            {/* <div className="cn__stat">
              <div className="cn__stat-dot" style={{ background: "#3b82f6" }} />
              <div>
                <div className="cn__stat-label">Pending</div>
                <div className="cn__stat-val">{pendingCnt}</div>
              </div>
            </div> */}
            <div className="cn__stat">
              <div className="cn__stat-dot" style={{ background: "#ef4444" }} />
              <div>
                <div className="cn__stat-label">Total Credited</div>
                <div className="cn__stat-val" style={{ fontSize: 13 }}>{formatCurrency(totalAmt)}</div>
              </div>
            </div>
          </div>

          {/* ── Main Card ── */}
          <div className="cn__card">

            {/* Toolbar */}
            <div className="cn__toolbar">
              <div className="cn__toolbar-left">
                <span className="cn__show-lbl">Show</span>
                <select className="cn__select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(+e.target.value); setCurrentPage(1); }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
                <span className="cn__show-lbl">entries</span>
              </div>
              <div className="cn__toolbar-right">
                <div className="cn__search">
                  <FiSearch className="cn__search-icon" />
                  <input
                    className="cn__search-input"
                    placeholder="Search notes, customers…"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="cn__table-wrap">
              <table className="cn__table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort("credit_note_number")}>
                      Note ID <SortIcon columnKey="credit_note_number" />
                    </th>
                    <th className="sortable" onClick={() => handleSort("customer_name")}>
                      Customer <SortIcon columnKey="customer_name" />
                    </th>
                    <th>Invoice</th>
                    <th className="sortable" onClick={() => handleSort("credit_date")}>
                      Date <SortIcon columnKey="credit_date" />
                    </th>
                    <th className="sortable" style={{ textAlign: "right" }} onClick={() => handleSort("credit_amount")}>
                      Amount <SortIcon columnKey="credit_amount" />
                    </th>
                    <th>Status</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="cn__shimmer-row">
                        {Array.from({ length: 7 }).map((__, j) => (
                          <td key={j}><div className="cn__shimmer-cell" style={{ width: j === 4 ? "80%" : "90%" }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : paginated.length > 0 ? paginated.map((cn) => (
                    <tr key={cn.id}>
                      <td><span className="cn__note-id"><FiFileText size={11} />{cn.credit_note_number}</span></td>
                      <td>
                        <div className="cn__customer">
                          <div className="cn__cust-avatar">{getInitials(cn.customer_name)}</div>
                          <span className="cn__cust-name">{cn.customer_name}</span>
                        </div>
                      </td>
                      <td><span className="cn__inv-chip">{cn.invoice_number}</span></td>
                      <td>
                        <span className="cn__date">
                          {new Date(cn.credit_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <span className="cn__amount">{formatCurrency(cn.credit_amount)}</span>
                      </td>
                      <td><StatusBadge status={cn.status} /></td>
                      <td>
                        <div className="cn__actions">
                          <button className="cn__action-btn" title="View" onClick={() => {}}>
                            <FiEye size={14} />
                          </button>
                          <button className="cn__action-btn" title="Edit" onClick={() => setEditData(cn)}>
                            <FiEdit size={14} />
                          </button>
                          <button className="cn__action-btn" title="Print" onClick={() => {}}>
                            <FiPrinter size={14} />
                          </button>
                          <button className="cn__action-btn cn__action-btn--danger" title="Delete" onClick={() => handleDelete(cn.id, cn.invoice_id)}>
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7">
                        <div className="cn__empty">
                          <div className="cn__empty-icon">📄</div>
                          <div className="cn__empty-title">No credit notes found</div>
                          <div className="cn__empty-sub">Try adjusting your search or create a new credit note.</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="cn__footer">
              <div className="cn__footer-info">
                Showing <strong>{processedCreditNotes.length > 0 ? startIndex + 1 : 0}</strong> to{" "}
                <strong>{Math.min(startIndex + itemsPerPage, processedCreditNotes.length)}</strong> of{" "}
                <strong>{processedCreditNotes.length}</strong> entries
              </div>
              <CustomPagination
                totalItems={processedCreditNotes.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

        </div>
      </main>
    </>
  );
}

export default CreditNote;
